import { ABOVE_ALL_CLS, SIDEBAR_ALLOWED_OVERLAY_CLS } from '@common/constants';
import { useStateUpdateEnabled } from '@common/performance/StateUpdateEnabledContext';
import { IDEEntity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { Markdown } from '@prophecy/ui-v3';
import { Dialog } from '@prophecy/ui-v3/Dialog';
import { FileDiagnostic, RangeDefinition } from '@prophecy/ui-v3/Editor/types';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { matchesPropertyPath } from '@prophecy/utils/data';
import { isEqual } from 'lodash';
import { useRef } from 'react';

import { TrackEventType } from '../context/mixpanel/types';
import { LogCategory } from '../generic-components/Logger/LogCategory';
import { DiagnosticLogMetadata, Log } from '../generic-components/Logger/types';
import {
  LSP_SEVERITY_KEYS,
  LSP_TO_MONACO_SEVERITY,
  LSP_Severity_To_LogType,
  MonacoSeverityToLogType
} from './constants';
import { useAllDiagnostics } from './DiagnosticProvider';
import { DiagnosticType } from './types';

import type { Diagnostic, Range } from './types';

const lspToMonacoSeverity = (severity: LSP_SEVERITY_KEYS) => LSP_TO_MONACO_SEVERITY[severity];

export function lspToMonacoRange(range: Range): RangeDefinition {
  return {
    from: {
      line: range.start.line + 1,
      column: range.start.character + 1
    },
    to: { line: range.end.line + 1, column: range.end.character + 1 }
  };
}
function monacoRangeToLogRange(range: RangeDefinition): Range {
  return {
    start: {
      line: range.from.line,
      character: range.from.column
    },
    end: { line: range.to.line, character: range.to.column }
  };
}

export function lspToMonacoDiagnostics<P extends Pick<Diagnostic, 'range' | 'severity'>>(diagnostics: P[]) {
  return diagnostics.map(lspToMonacoDiagnostic);
}
export function lspToMonacoDiagnostic<P extends Pick<Diagnostic, 'range' | 'severity'>>({
  severity,
  range,
  ...rest
}: P) {
  return {
    ...rest,
    severity: lspToMonacoSeverity(severity),
    range: lspToMonacoRange(range)
  };
}

export function useDiagnosticsForPath(
  propertyPath: string[],
  ignorePaths: string[] = [],
  getLocalProperty?: (propertyPath: string) => string
) {
  const propertyPaths = propertyPath;
  const loadAllDiagnostics = propertyPath.length === 1 && propertyPath[0] === '';

  const stateUpdateEnabled = useStateUpdateEnabled();
  const selectedDiagnosticsRef = useRef<Diagnostic[]>();

  return useAllDiagnostics((newDiagnostics) => {
    if (!stateUpdateEnabled && selectedDiagnosticsRef.current !== undefined) {
      return selectedDiagnosticsRef.current;
    }

    let errors: Diagnostic[] = [];

    if (loadAllDiagnostics && ignorePaths.length === 0 && !getLocalProperty) {
      errors = newDiagnostics;
    } else {
      newDiagnostics.forEach((diagnostic) => {
        if (diagnostic.property) {
          if (ignorePaths.some((path) => matchesPropertyPath(diagnostic.property, path))) {
            return;
          }
          if (loadAllDiagnostics || propertyPaths.some((path) => matchesPropertyPath(diagnostic.property, path))) {
            let property = diagnostic.property;
            // for components we follow localized property
            if (getLocalProperty) {
              property = getLocalProperty(property);
            }
            errors.push({ ...diagnostic, property });
          }
        }
      });
    }

    // if errors are not changed, don't update the state
    if (errors.length === 0) {
      errors = FROZEN_EMPTY_ARRAY as Diagnostic[];
    }

    selectedDiagnosticsRef.current = errors;

    return errors;
  }, isEqual);
}

export function useDiagnosticsLogs() {
  const diagnostics = useDiagnosticsForPath(['']);
  return propertyDiagnosticsToLog(diagnostics);
}

export function sortFixableLogs(logs: Log[]) {
  return logs.sort((a, b) => {
    const asIsFixable = Boolean((a.metadata as DiagnosticLogMetadata)?.diagnostic?.diagnosticFix);
    const bsIsFixable = Boolean((b.metadata as DiagnosticLogMetadata)?.diagnostic?.diagnosticFix);

    if (asIsFixable && !bsIsFixable) {
      return -1;
    }
    if (!asIsFixable && bsIsFixable) {
      return 1;
    }
    return 0;
  });
}

/**
 * This function sorts an array of logs so that logs whose metadata property
 * starts with the specified currentGraphPath appear first in the array.
 * Logs that don't match the current graph path are moved to the end.
 *
 * @param logs - Array of Log objects to be sorted
 * @param currentGraphPath - The graph path prefix to prioritize in sorting
 * @returns A new sorted array where logs matching the current graph path come first
 */
export function sortLogsByCurrentGraphPath(logs: Log[], currentGraphPath: string) {
  return logs.sort((a, b) => {
    const aIsCurrentGraphPath = a.metadata?.property?.startsWith(currentGraphPath) ? 1 : 0;
    const bIsCurrentGraphPath = b.metadata?.property?.startsWith(currentGraphPath) ? 1 : 0;
    return bIsCurrentGraphPath - aIsCurrentGraphPath;
  });
}

export function propertyDiagnosticsToLog(diagnostics: Diagnostic[]): Log[] {
  return diagnostics.map((d) => {
    const type = LSP_Severity_To_LogType[d.severity];
    const { line, character: column } = d.range.start;
    return {
      category: LogCategory.diagnostics,
      type,
      message: d.diagnosticType === DiagnosticType.Markdown ? <Markdown>{d.message}</Markdown> : d.message,
      multiline: d.diagnosticType === DiagnosticType.Markdown,
      metadata: { property: d.property, line, column, diagnostic: d, isStale: d.metadata?.isStale },
      source: d.source,
      range: d.range,
      detailedMessage: d.detailedMsg
    };
  });
}
export function fileDiagnosticsToLog(filePath: string, diagnostics: FileDiagnostic[]): Log[] {
  return diagnostics.map((d) => {
    const type = MonacoSeverityToLogType[d.severity];
    const { line, column } = d.range.from;
    return {
      category: LogCategory.diagnostics,
      type,
      message: d.message,
      metadata: { filePath, line, column },
      range: monacoRangeToLogRange(d.range)
    };
  });
}

export function ideLSPConnectionAlert(
  reconnectLSPClient: () => void,
  isActorDead: boolean,
  track: TrackEventType,
  entity: IDEEntity,
  networkChanged?: boolean,
  uid?: string
) {
  const dialogOverlayCls = `${SIDEBAR_ALLOWED_OVERLAY_CLS} ${ABOVE_ALL_CLS}`;
  const dialogCls = ABOVE_ALL_CLS;
  const isInactive = document.hidden;

  if (isInactive && !isActorDead) {
    track(EntityEvents[entity].ide.inActivityDisconnect, { uid, entity });
    document.addEventListener(
      'visibilitychange',
      () => {
        toast.info({
          content: 'Prophecy disconnected due to a period of inactivity. Attempting to reconnect...',
          width: `500px`,
          duration: 5000,
          placement: 'top'
        });
        reconnectLSPClient();
      },
      { once: true }
    );

    return;
  }

  const showNetworkMessage = networkChanged && !isActorDead;

  track(
    showNetworkMessage ? EntityEvents[entity].ide.netWorkChangeDisconnect : EntityEvents[entity].ide.errorDisconnect,
    {
      uid,
      entity
    }
  );

  Dialog.alert({
    'data-test-id': 'lsp-disconnected-alert-dialog',
    title: showNetworkMessage ? 'Prophecy got disconnected.' : 'Something went wrong.',
    children: showNetworkMessage ? (
      'Prophecy disconnected due to the network change. As a result, some recent changes may be lost. Please reconnect to continue.'
    ) : (
      <p>
        Prophecy is unable to connect so some changes may be lost.
        <br />
        Please reconnect and reach out to Prophecy Support if the issue persists.
      </p>
    ),
    iconPlacement: 'right',
    className: dialogCls,
    overlayClassName: dialogOverlayCls,
    closable: false,
    okButton: { children: 'Reconnect' },
    onOk: reconnectLSPClient,
    closeButton: null
  });
}
